import React from "react";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { DashboardLayout } from "@/components/layout";
import {
  Card,
  CardContent,
  PageHeader,
  StatusBadge,
  Switch,
  showToast,
} from "@/components/ui";
import { useMarketplacesQuery } from "@/graphql/generated";
import { adminGqlRequest } from "@/lib/graphql";

const SET_MARKETPLACE_STATUS = /* GraphQL */ `
  mutation SetMarketplaceStatus($input: SetMarketplaceStatusInput!) {
    setMarketplaceStatus(input: $input) {
      id
      code
      status
    }
  }
`;

export default function Marketplaces() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useMarketplacesQuery();

  const marketplaces = [...(data?.marketplaces ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const handleToggle = async (
    code: string,
    name: string,
    nextEnabled: boolean,
  ) => {
    try {
      await adminGqlRequest(SET_MARKETPLACE_STATUS, {
        input: { code, status: nextEnabled ? "ACTIVE" : "INACTIVE" },
      });
      await queryClient.invalidateQueries({
        queryKey: useMarketplacesQuery.getKey(),
      });
      showToast.success(
        nextEnabled ? "Marketplace enabled" : "Marketplace disabled",
        `${name} is now ${nextEnabled ? "visible to" : "hidden from"} sellers.`,
      );
    } catch (err) {
      showToast.error(
        "Update failed",
        (err as Error).message || "Please try again.",
      );
      throw err;
    }
  };

  return (
    <DashboardLayout activePath="/marketplaces">
      <PageHeader
        title="Marketplaces"
        subtitle="Enable or disable marketplaces for sellers"
        breadcrumbs={[{ label: "Marketplaces", active: true }]}
      />

      <div className="max-w-2xl">
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : marketplaces.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">
                No marketplaces found.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {marketplaces.map((mp) => {
                  const enabled = mp.status === "ACTIVE";
                  return (
                    <li
                      key={mp.id}
                      className="flex items-center justify-between gap-4 px-5 py-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {mp.name}
                        </span>
                        <StatusBadge status={mp.status} />
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={
                          mp.status !== "ACTIVE" && mp.status !== "INACTIVE"
                        }
                        aria-label={`Toggle ${mp.name}`}
                        onCheckedChange={(next) =>
                          handleToggle(mp.code, mp.name, next)
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
          Disabled marketplaces are hidden from sellers in onboarding and in
          marketplace settings.
        </p>
      </div>
    </DashboardLayout>
  );
}
